import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, X, GripVertical, Eye, EyeOff, ExternalLink, Upload, Image } from 'lucide-react';
import { toast } from 'sonner';
import { getImageUrl } from '../../utils/api';
import './AdminStyles.css';
import './AMRLinksManager.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_OPTIONS = [
  { value: 'spotify', label: 'Spotify' },
  { value: 'apple', label: 'Apple Music' },
  { value: 'soundcloud', label: 'SoundCloud' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'beatport', label: 'Beatport' },
  { value: 'bandcamp', label: 'Bandcamp' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'twitter', label: 'X / Twitter' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'globe', label: 'Website' },
  { value: 'link', label: 'Generic Link' },
  { value: 'calendar', label: 'Calendar / Event' },
];

const CATEGORY_OPTIONS = [
  { value: 'featured', label: 'Featured (top of page)' },
  { value: 'music', label: 'Music' },
  { value: 'social', label: 'Social Media' },
  { value: 'custom', label: 'Other / Custom' },
];

const AMRLinksManager = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    title: '', url: '', icon: 'link', category: 'custom', sort_order: 0, is_active: true
  });

  const getToken = () => localStorage.getItem('adminToken');

  const fetchLinks = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/amr-links`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (res.ok) setLinks(await res.json());
    } catch { toast.error('Failed to load links'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  const resetForm = () => {
    setFormData({ title: '', url: '', icon: 'link', category: 'custom', sort_order: 0, is_active: true });
    setEditing(null);
  };

  const openModal = (link = null) => {
    if (link) {
      setEditing(link);
      setFormData({
        title: link.title, url: link.url, icon: link.icon || 'link',
        category: link.category || 'custom', sort_order: link.sort_order || 0, is_active: link.is_active !== false
      });
    } else {
      resetForm();
      setFormData(prev => ({ ...prev, sort_order: links.length }));
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = getToken();
    try {
      const url = editing
        ? `${API_URL}/api/admin/amr-links/${editing.id}`
        : `${API_URL}/api/admin/amr-links`;
      const res = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(formData)
      });
      if (!res.ok) throw new Error('Failed to save');
      toast.success(editing ? 'Link updated' : 'Link added');
      setShowModal(false);
      resetForm();
      fetchLinks();
    } catch (err) { toast.error(err.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this link?')) return;
    try {
      await fetch(`${API_URL}/api/admin/amr-links/${id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('Link deleted');
      fetchLinks();
    } catch { toast.error('Delete failed'); }
  };

  const toggleActive = async (link) => {
    try {
      await fetch(`${API_URL}/api/admin/amr-links/${link.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ is_active: !link.is_active })
      });
      fetchLinks();
    } catch { toast.error('Failed to toggle'); }
  };

  const moveLink = async (link, direction) => {
    const sorted = [...links].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const idx = sorted.findIndex(l => l.id === link.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;

    const token = getToken();
    await Promise.all([
      fetch(`${API_URL}/api/admin/amr-links/${sorted[idx].id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sort_order: swapIdx })
      }),
      fetch(`${API_URL}/api/admin/amr-links/${sorted[swapIdx].id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sort_order: idx })
      })
    ]);
    fetchLinks();
  };

  const handleIconUpload = async (linkId, file) => {
    const token = getToken();
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API_URL}/api/admin/amr-links/${linkId}/icon`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd
      });
      if (!res.ok) throw new Error('Upload failed');
      toast.success('Icon uploaded');
      fetchLinks();
    } catch (err) { toast.error(err.message); }
  };

  const grouped = {
    featured: links.filter(l => l.category === 'featured'),
    music: links.filter(l => l.category === 'music'),
    social: links.filter(l => l.category === 'social'),
    custom: links.filter(l => l.category === 'custom'),
  };

  if (loading) return <div className="loading">Loading links...</div>;

  return (
    <div className="amr-links-mgr management-page" data-testid="amr-links-manager">
      <div className="page-header">
        <div>
          <h1>AMR Links Page</h1>
          <p className="amr-links-mgr__subtitle">Manage links for <a href="/amr-links" target="_blank" rel="noreferrer">/amr-links</a></p>
        </div>
        <button className="btn-primary" onClick={() => openModal()} data-testid="add-link-btn">
          <Plus size={16} /> Add Link
        </button>
      </div>

      <p className="amr-links-mgr__info">
        Releases, promos, and events are auto-pulled from your existing content. Add custom links below for platforms, playlists, merch, etc.
      </p>

      {links.length === 0 ? (
        <div className="amr-links-mgr__empty">
          <ExternalLink size={40} />
          <h3>No custom links yet</h3>
          <p>Add your Spotify, SoundCloud, merch store, and other links here.</p>
        </div>
      ) : (
        Object.entries(grouped).map(([cat, items]) => items.length > 0 && (
          <div key={cat} className="amr-links-mgr__group" data-testid={`link-group-${cat}`}>
            <h3 className="amr-links-mgr__group-title">{CATEGORY_OPTIONS.find(c => c.value === cat)?.label || cat}</h3>
            {items.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map(link => (
              <div key={link.id} className={`amr-links-mgr__row ${!link.is_active ? 'inactive' : ''}`} data-testid={`link-row-${link.id}`}>
                <div className="amr-links-mgr__row-grip">
                  <button onClick={() => moveLink(link, 'up')} title="Move up"><GripVertical size={14} /></button>
                </div>
                {link.icon_image ? (
                  <img src={getImageUrl(link.icon_image)} alt="" className="amr-links-mgr__row-thumb" />
                ) : (
                  <span className="amr-links-mgr__row-icon-badge">{link.icon}</span>
                )}
                <div className="amr-links-mgr__row-info">
                  <span className="amr-links-mgr__row-title">{link.title}</span>
                  <span className="amr-links-mgr__row-url">{link.url}</span>
                </div>
                <div className="amr-links-mgr__row-actions">
                  <label title="Upload icon" className="amr-links-mgr__icon-upload">
                    <Image size={14} />
                    <input type="file" accept="image/*" hidden onChange={e => e.target.files[0] && handleIconUpload(link.id, e.target.files[0])} />
                  </label>
                  <button onClick={() => toggleActive(link)} title={link.is_active ? 'Hide' : 'Show'}>
                    {link.is_active ? <Eye size={14} /> : <EyeOff size={14} />}
                  </button>
                  <button onClick={() => openModal(link)} title="Edit"><Edit size={14} /></button>
                  <button onClick={() => handleDelete(link.id)} className="danger" title="Delete"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        ))
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} data-testid="link-modal">
            <div className="modal-header">
              <h2>{editing ? 'Edit Link' : 'Add Link'}</h2>
              <button className="close-btn" onClick={() => { setShowModal(false); resetForm(); }}><X size={20} /></button>
            </div>
            <form className="modal-form" onSubmit={handleSubmit} data-testid="link-form">
              <div className="form-group">
                <label>Title *</label>
                <input value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })}
                  required placeholder="e.g. Spotify Playlist" data-testid="link-title-input" />
              </div>
              <div className="form-group">
                <label>URL *</label>
                <input value={formData.url} onChange={e => setFormData({ ...formData, url: e.target.value })}
                  required placeholder="https://..." data-testid="link-url-input" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Icon</label>
                  <select value={formData.icon} onChange={e => setFormData({ ...formData, icon: e.target.value })}
                    data-testid="link-icon-select">
                    {ICON_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <select value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })}
                    data-testid="link-category-select">
                    {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input type="checkbox" checked={formData.is_active}
                    onChange={e => setFormData({ ...formData, is_active: e.target.checked })} />
                  Active (visible on public page)
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>Cancel</button>
                <button type="submit" className="btn-primary" data-testid="link-save-btn">
                  {editing ? 'Update' : 'Add Link'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AMRLinksManager;
